import * as _ from 'underscore';

import { Request, IRequestOptions } from 'Core/Common/Request';
import { P } from 'Core/Common/Promise';
import { Deserialize } from 'libs/cerialize';
import { EditTemplateModel } from 'Core/Controls/TemplateDesigner/Models/EditTemplateModel';
import { TemplateLanguageModel } from 'Core/Controls/TemplateDesigner/Models/TemplateLanguageModel';

/** The server does not always answer an error with JSON - an empty body or an HTML
 *  error page both arrive here. JSON.parse then THREW inside the failure handler,
 *  which left the deferred neither resolved nor rejected: the caller's
 *  .always(() => BlockUI.Unblock()) never ran and the designer stayed behind its
 *  loader. Fall back to the raw text instead of throwing. */
function failureMessage(raw: string): string {
	if (!raw) return 'Request failed';
	try {
		return JSON.parse(raw).Message || raw;
	} catch {
		return raw;
	}
}

export interface IGetLanguagesDto {
	SubjectEntityId: number;
	SubjectRecordId: number;
	ControlId: number;
};

export interface ICreateTemplateDto {
	SubjectEntityId: number;
	SubjectRecordId: number;
	LanguageId: number;
	ControlId: number;
};

export interface IEditTemplateDto {
	SubjectEntityId: number;
	SubjectRecordId: number;
	LanguageId: number;
	ControlId: number;
};

export interface ISaveTemplateDto {
	FileName: string;
	Content: string;
	QueryId: number;
	ControlId: number;
	RecordId: number;
	KSeq: number;
};

export interface IPreviewHtmlTemplateDto {
	Data: string;
	RecordId: number;
	QueryId: number;
};

// Names the template file version Edit has just created, which is the one the
// editor opens - not the template record itself.
export interface IEditorConfigDto {
	ControlId: number;
	SubjectRecordId: number;
	KSeq: number;
	// The template's own name. The file the server spools has none of its own, and
	// the extension is what decides which editor opens.
	FileName: string;
};

/** One record merged into the template, to be shown read-only in the editor. The document itself never travels through the browser - only the session that opens it. */
export interface IPreviewDocumentTemplateDto {
	/** The template is named the way the editor names it - the browser has uploaded nothing for the server to merge, so it cannot pass a temp file the way the desktop client does. */
	ControlId: number;
	SubjectRecordId: number;
	KSeq: number;
	FileName: string;
	Extension: string;
	/** The record whose values are merged in. */
	RecordId: number;
	QueryId: number;
}

export interface ICollaboraConfig {
	iframeSrc: string;
	accessToken: string;
	fileId: string;
	fileName: string;
};

// The editing session whose bytes are to be saved, and the template file version
// they belong in. The document itself never travels through the browser.
export interface ISaveFromEditorDto {
	FileId: string;
	AccessToken: string;
	ControlId: number;
	RecordId: number;
	KSeq: number;
	QueryId: number;
	FileName: string;
};

export class TemplateDesignerStore {

	static GetLanguages(params: IGetLanguagesDto): P.Promise<Array<TemplateLanguageModel>> {
		var deferredResult = P.defer<Array<TemplateLanguageModel>>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/GetLanguages',
				dataType: 'json',
				type: 'GET'
			}, params: params
		};

		Request.Send(requestParams)
			.then(data => {
					deferredResult.resolve(Deserialize(data, TemplateLanguageModel));
			}).fail(data => { deferredResult.reject({ message: failureMessage(data.message) }) });

		return deferredResult.promise();
	}

	static Edit(params: IEditTemplateDto): P.Promise<EditTemplateModel> {
		var deferredResult = P.defer<EditTemplateModel>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/Edit',
				dataType: 'json',
				type: 'POST'
			}, params: params
		};

		Request.Send(requestParams)
			.then(data => {
				deferredResult.resolve(Deserialize(data, EditTemplateModel));
			}).fail(data => { deferredResult.reject({ message: failureMessage(data.message) }) });

		return deferredResult.promise();
	}

	// Word and Excel templates carry no markup for the browser to hold: the editor
	// fetches the file itself over WOPI, so this only says which editor to load and
	// under which access token.
	static GetTemplateEditorConfig(params: IEditorConfigDto): P.Promise<ICollaboraConfig> {
		var deferredResult = P.defer<ICollaboraConfig>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/GetTemplateEditorConfig',
				dataType: 'json',
				type: 'GET'
			}, params: params
		};

		Request.Send(requestParams)
			.then(data => { deferredResult.resolve(data); })
			.fail(data => { deferredResult.reject({ message: failureMessage(data.message) }) });

		return deferredResult.promise();
	}

	static SaveFromEditor(params: ISaveFromEditorDto): P.Promise<void> {
		var deferredResult = P.defer<void>();

		var requestParams: IRequestOptions = {
			proxy: {
				/** No dataType: the action answers Ok() with no body, and asking jQuery for JSON turns that into a parse error - the failure handler then throws on JSON.parse, the promise never settles and the designer stays blocked behind its own loader. */
				url: '/api/DocumentTemplatesApi/SaveFromEditor',
				type: 'POST'
			}, params: params
		};

		Request.Send(requestParams)
			.then(() => { deferredResult.resolve(null); })
			.fail(data => { deferredResult.reject({ message: failureMessage(data.message) }) });

		return deferredResult.promise();
	}

	static Create(params: ICreateTemplateDto): P.Promise<EditTemplateModel> {
		var deferredResult = P.defer<EditTemplateModel>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/Create',
				type: 'POST'
			}, params: params
		};

		Request.Send(requestParams)
		.then((data) => deferredResult.resolve(Deserialize(data, EditTemplateModel)))
		.fail(err => { deferredResult.reject({ message: failureMessage(err.message) }) });

		return deferredResult.promise();
	}

	static Save(params: ISaveTemplateDto): P.Promise<any> {
		var deferredResult = P.defer<any>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/Save',
				type: 'POST'
			}, params: params
		};

		Request.Send(requestParams)
		.then(() => deferredResult.resolve(null))
		.fail(err => { deferredResult.reject({ message: failureMessage(err.message) }) });

		return deferredResult.promise();
	}

	static PreviewInEditor(params: IPreviewDocumentTemplateDto): P.Promise<ICollaboraConfig> {
		var deferredResult = P.defer<ICollaboraConfig>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/PreviewInEditor',
				type: 'POST'
			}, params: params
		};

		Request.Send(requestParams)
			.then((data) => { deferredResult.resolve(data as ICollaboraConfig); })
			.fail(err => { deferredResult.reject({ message: err.message }); });

		return deferredResult.promise();
	}

	static Preview(params: IPreviewHtmlTemplateDto): P.Promise<string> {
		var deferredResult = P.defer<string>();

		var requestParams: IRequestOptions = {
			proxy: {
				url: '/api/DocumentTemplatesApi/PreviewHtmlTemplate',
				type: 'POST'
			}, params: params
		};

		Request.Send(requestParams)
		.then((data) => {
			deferredResult.resolve(Deserialize(data, String))
		})
		.fail(err => { deferredResult.reject({ message: failureMessage(err.message) }) });

		return deferredResult.promise();
	}
}